import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API, useBranding } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Loader2, Lock, CheckCircle, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';

const ResetPasswordPage = () => {
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const { theme } = useTheme();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    if (!token) setTokenError('Missing reset token. Please use the link from your email.');
  }, [token]);

  const passwordStrength = () => {
    if (!password) return null;
    if (password.length < 8) return { label: 'Too short', color: 'text-red-400', bar: 'bg-red-500', w: '25%' };
    if (password.length < 10 || !/[0-9]/.test(password)) return { label: 'Fair', color: 'text-amber-400', bar: 'bg-amber-500', w: '50%' };
    if (!/[A-Z]/.test(password) || !/[^a-zA-Z0-9]/.test(password)) return { label: 'Good', color: 'text-blue-400', bar: 'bg-blue-500', w: '75%' };
    return { label: 'Strong', color: 'text-emerald-400', bar: 'bg-emerald-500', w: '100%' };
  };

  const strength = passwordStrength();
  const mismatch = confirm && password !== confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      if (detail.includes('expired') || detail.includes('Invalid')) {
        setTokenError(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />

      <div className="w-full max-w-md relative z-10">
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Sign In
        </Link>

        <div className="flex items-center gap-2 mb-8">
          {activeLogo ? (
            <img src={activeLogo} alt={brand_name} className="h-9 max-w-[140px] object-contain" />
          ) : (
            <>
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <span className="font-bold text-2xl">{brand_name || 'CoinPayUp'}</span>
            </>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-8">

          {/* Invalid/expired token */}
          {tokenError ? (
            <div className="text-center space-y-4" data-testid="reset-token-error">
              <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Link Invalid or Expired</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{tokenError}</p>
              <Link to="/forgot-password">
                <Button className="w-full bg-primary text-white mt-2" data-testid="request-new-link-btn">
                  Request a New Link
                </Button>
              </Link>
            </div>

          ) : success ? (
            /* Success state */
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Password Reset!</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your password has been updated successfully. Redirecting to sign in...
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="go-to-login-btn">
                  Go to Sign In
                </Button>
              </Link>
            </div>

          ) : (
            /* Form */
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <h1 className="text-xl font-bold text-foreground">Set new password</h1>
                <p className="text-sm text-muted-foreground mt-1">Choose a strong password for your account.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New password */}
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPw ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Strength bar */}
                  {strength && (
                    <div className="space-y-1">
                      <div className="h-1 w-full bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${strength.bar} rounded-full transition-all`} style={{ width: strength.w }} />
                      </div>
                      <p className={`text-xs ${strength.color}`}>{strength.label}</p>
                    </div>
                  )}
                </div>

                {/* Confirm password */}
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Re-enter password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      className={`pr-10 ${mismatch ? 'border-red-500/60 focus:border-red-500' : ''}`}
                      data-testid="confirm-password-input"
                    />
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {mismatch && <p className="text-xs text-red-400">Passwords do not match</p>}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-white"
                  disabled={loading || !!mismatch || password.length < 8}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Resetting...</> : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
